import React from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { Alert, AlertDescription, AlertTitle, Box, Button } from "@salestrak/ui";

import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAuthEmail } from "#app/lib/hooks/use-auth-email";
import { useInvitationByToken } from "#app/lib/hooks/use-invitation-by-token";
import { useLocalSearchParams } from "#app/lib/hooks/router-compat";
import { PageLoading } from "#app/components/loading/page-loading";
import { WorkspaceInviteScreen } from "#app/components/workspace-invite/workspace-invite-screen";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import { AuthLayout } from "./auth-layout";
import { AcceptInviteForm } from "./accept-invite-form";
import { routePath } from "#app/lib/hooks/use-app-navigate";

/**
 * The emailed invite link, opened by someone who is already signed in.
 *
 * The token proves the invite; the session proves who is looking. When the
 * two agree, the workspace row the invite created is already in the
 * account's workspace list, so the link resolves to that membership: an
 * `invited` (or declined) row opens the Join / Decline screen, an `active`
 * row means the invite was already taken up and the link is a door into the
 * workspace. Neither asks for a password.
 *
 * When they disagree — the invite was sent to another address — the link
 * falls back to the credentials form, which signs in as that address and
 * replaces this session. The form says so first.
 */
export function SignedInAcceptInvite() {
  const { token } = useLocalSearchParams<{ token?: string }>();
  const navigate = useNavigate();
  const sessionEmail = useAuthEmail();
  const { loading, details, invalid, invalidReason } = useInvitationByToken(token);
  const {
    workspaces,
    isLoading: workspacesLoading,
    refreshWorkspaces,
    acceptInvitation,
    declineInvitation,
  } = useWorkspace();

  const matchesSession =
    details !== null &&
    sessionEmail !== null &&
    details.email.toLowerCase() === sessionEmail.toLowerCase();
  const membership = matchesSession
    ? workspaces.find((w) => w.workspaceId === details.workspaceId)
    : undefined;

  // The invite may be newer than the account's workspace list (sent after
  // the app loaded). One refetch closes that gap; a row still missing after
  // it means the invite was cancelled between the token read and now.
  const [refetched, setRefetched] = React.useState(false);
  React.useEffect(() => {
    if (!matchesSession || membership || workspacesLoading || refetched) return;
    setRefetched(true);
    void refreshWorkspaces();
  }, [matchesSession, membership, workspacesLoading, refetched, refreshWorkspaces]);

  if (loading || (matchesSession && !membership && (workspacesLoading || !refetched))) {
    return <PageLoading delayed />;
  }

  if (invalid || (matchesSession && !membership)) {
    const expired = invalidReason === "expired";
    return (
      <WorkspaceScreenLayout email={sessionEmail}>
        <Box direction="column" gap="lg">
          <Alert variant="error">
            <AlertTitle>
              {expired ? "Invitation expired" : "Invitation no longer valid"}
            </AlertTitle>
            <AlertDescription>
              {expired
                ? "This invitation link has expired. Contact your workspace admin to request a new invite."
                : "This invitation has been cancelled or is no longer active. Contact your workspace admin to request a new invite."}
            </AlertDescription>
          </Alert>
          <Button size="xl" shape="square" onClick={() => navigate({ to: "/" })}>
            Back to SalesTrak
          </Button>
        </Box>
      </WorkspaceScreenLayout>
    );
  }

  if (!matchesSession || !membership) {
    return (
      <AuthLayout
        heading="Accept Invitation"
        legalAction="accepting an invitation"
        subheading="This invitation was sent to a different account."
        footerText="Not you?"
        footerLinkText="Log in"
        footerLinkHref="/login"
      >
        <AcceptInviteForm signedInEmail={sessionEmail} />
      </AuthLayout>
    );
  }

  if (membership.memberStatus === "active") {
    return <Navigate to={routePath(`/${membership.urlPath}`)} replace />;
  }

  const id = membership.workspaceId;
  return (
    <WorkspaceInviteScreen
      workspace={membership}
      via="email_link"
      onJoin={async () => {
        // acceptInvitation refetches and switches the active workspace; the
        // index route then lands on the user's default page there.
        await acceptInvitation(id, "email_link");
        navigate({ to: "/", replace: true });
      }}
      onDecline={async () => {
        await declineInvitation(id, "email_link");
        navigate({ to: "/", replace: true });
      }}
    />
  );
}
